import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import { DatasetCard } from "@app/components/DatasetCard";
import {
  DeleteFolderDialog,
  FolderNameDialog,
  MoveToFolderDialog,
} from "@app/components/FolderDialogs";
import {
  FolderActionsMenu,
  FolderDropBar,
  FolderTile,
} from "@app/components/FolderShelf";
import {
  ArrowLeft,
  FolderClosed,
  FolderPlus,
  Inbox,
  Loader2,
  Search,
  SortAsc,
  SortDesc,
} from "lucide-react";
import API from "@app/services/api";
import { useViewAsKey } from "@app/services/view-as";
import { useLabReadOnly } from "@app/utils/use-lab-read-only";
import {
  usePaginatedSearch,
  type SearchPage,
} from "@app/utils/use-paginated-search";
import {
  UNFILED,
  createFolder,
  deleteFolder,
  fetchShelf,
  fileDataset,
  renameFolder,
  type DatasetFolder,
  type DatasetShelf,
} from "@app/utils/dataset-folders";
import { useFolderDrop } from "@app/utils/use-folder-drop";
import { Plural, Trans, useLingui } from "@lingui/macro";
import type { DatasetState } from "@app/utils/reference-dataset";
import { toast } from "sonner";

interface Dataset {
  id: string;
  name: string;
  description: string;
  file_count: number;
  example_count: number;
  created_at: string;
  updated_at: string;
  specialite: string | null;
  folder_id: string | null;
  state?: DatasetState;
}

type SortField = "name" | "created_at" | "example_count";
type SortDir = "asc" | "desc";

// Full rows in both the two- and three-column grids.
const PAGE_SIZE = 12;

export default function DatasetsPage() {
  const { t } = useLingui();
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const viewAsKey = useViewAsKey();
  const labReadOnly = useLabReadOnly();

  // The open classeur lives in the URL, so the back button and a shared link land on it.
  const [searchParams, setSearchParams] = useSearchParams();
  const openFolderId = searchParams.get("classeur");
  const showFolder = (folderId: string | null) =>
    setSearchParams(folderId ? { classeur: folderId } : {});

  // The classeurs, their counts and « Sans classeur »'s, re-read after every gesture that moves one.
  const [shelf, setShelf] = useState<DatasetShelf | null>(null);
  const [shelfFailed, setShelfFailed] = useState(false);
  const reloadShelf = useCallback(() => {
    fetchShelf()
      .then((next) => {
        setShelf(next);
        setShelfFailed(false);
      })
      .catch(() => setShelfFailed(true));
  }, []);
  useEffect(() => {
    reloadShelf();
  }, [reloadShelf, viewAsKey]);

  // Bumped when the list on screen changed on the server (a classeur deleted from the shelf).
  const [listVersion, setListVersion] = useState(0);

  // In a classeur: its datasets. Outside: « Sans classeur », or every classeur when searching.
  const fetchPage = async (
    search: string,
    offset: number,
  ): Promise<SearchPage<Dataset>> => {
    const res = await API.get({
      path: "/dataset/",
      query: {
        search: search || undefined,
        folder: openFolderId ?? (search ? undefined : UNFILED),
        offset,
        limit: PAGE_SIZE,
        sort: sortField,
        order: sortDir,
      },
    });
    if (!res.ok || !res.data) throw new Error(res.error);
    return {
      rows: res.data.datasets as Dataset[],
      total: res.data.pagination.total as number,
    };
  };
  const {
    searchValue: search,
    setSearchValue: setSearch,
    items: datasets,
    total,
    loading,
    failed,
    loadMore,
    updateItems,
    removeItems,
  } = usePaginatedSearch({
    open: true,
    fetchPage,
    resetKey: `${sortField}|${sortDir}|${viewAsKey}|${openFolderId ?? ""}|${listVersion}`,
  });
  const searching = search.trim() !== "";

  const [naming, setNaming] = useState<{ folder: DatasetFolder | null } | null>(
    null,
  );
  const [deletingFolder, setDeletingFolder] = useState<DatasetFolder | null>(
    null,
  );
  const [moving, setMoving] = useState<Dataset | null>(null);

  const folders = shelf?.folders ?? [];
  const openFolder = openFolderId
    ? (folders.find((folder) => folder.id === openFolderId) ?? null)
    : null;
  const folderNames = new Map(
    folders.map((folder) => [folder.id, folder.name]),
  );
  const datasetTotal =
    (shelf?.unfiled_count ?? 0) +
    folders.reduce((sum, folder) => sum + folder.dataset_count, 0);

  const handleDeleteDataset = async (datasetId: string) => {
    const response = await API.delete({ path: `/dataset/${datasetId}` });
    if (response.ok) {
      removeItems((d) => d.id === datasetId);
      reloadShelf();
    } else {
      throw new Error(response.error || t`Erreur lors de la suppression`);
    }
  };

  const handleEditDataset = async (
    datasetId: string,
    updates: { name: string; description: string },
  ) => {
    const response = await API.put({
      path: `/dataset/${datasetId}`,
      body: { name: updates.name, description: updates.description },
    });
    if (response.ok) {
      const updated = response.data as Partial<Dataset> | undefined;
      updateItems((d) =>
        d.id === datasetId
          ? {
              ...d,
              name: updated?.name ?? updates.name,
              description: updated?.description ?? updates.description,
              updated_at: updated?.updated_at ?? d.updated_at,
            }
          : d,
      );
    } else {
      throw new Error(response.error || t`Erreur lors de l'enregistrement`);
    }
  };

  const handleMoved = (datasetId: string, folderId: string | null) => {
    // A search outside any classeur shows them all: the card stays, under its new one.
    const mixed = searching && openFolderId === null;
    if (!mixed && folderId !== openFolderId) {
      removeItems((d) => d.id === datasetId);
    } else {
      updateItems((d) =>
        d.id === datasetId ? { ...d, folder_id: folderId } : d,
      );
    }
    reloadShelf();
  };

  // Files a dataset straight from its card: a drop on a classeur, or « Retirer du classeur ».
  const fileFromCard = async (dataset: Dataset, folderId: string | null) => {
    try {
      await fileDataset(dataset.id, folderId);
      handleMoved(dataset.id, folderId);
      const name = dataset.name;
      const folderName = folderId ? folderNames.get(folderId) : undefined;
      toast.success(
        folderName
          ? t`« ${name} » rangé dans « ${folderName} »`
          : t`« ${name} » retiré de son classeur`,
      );
    } catch (err) {
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : t`Erreur lors de l'enregistrement`,
      );
    }
  };

  // Drag and drop: a card onto a classeur tile or the drop bar. UNFILED is « Sans classeur ».
  const drop = useFolderDrop<Dataset>({
    canDrop: (dataset, target) => {
      if (target === (dataset.folder_id ?? UNFILED)) return false;
      if (target === UNFILED) return true;
      // A classeur holds the datasets of its own speciality only.
      const folder = folders.find((f) => f.id === target);
      return folder !== undefined && folder.specialite === dataset.specialite;
    },
    onDrop: (dataset, target) =>
      void fileFromCard(dataset, target === UNFILED ? null : target),
  });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  if (shelfFailed || (failed && datasets.length === 0)) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <p className="text-red-500">
            <Trans>Erreur lors du chargement des jeux de données</Trans>
          </p>
        </div>
      </div>
    );
  }

  if (shelf === null) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <p className="text-slate-500">
            <Trans>Chargement des jeux de données...</Trans>
          </p>
        </div>
      </div>
    );
  }

  if (openFolderId && !openFolder) {
    return (
      <div className="p-6">
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <h3 className="text-lg font-semibold mb-2">
            <Trans>Classeur introuvable</Trans>
          </h3>
          <Button variant="outline" onClick={() => showFolder(null)}>
            <Trans>Tous les jeux de données</Trans>
          </Button>
        </div>
      </div>
    );
  }

  const SortIcon = sortDir === "asc" ? SortAsc : SortDesc;
  const newDatasetPath = openFolder
    ? `/datasets/new?classeur=${openFolder.id}`
    : "/datasets/new";
  const showsShelf = !openFolder && !searching && folders.length > 0;

  return (
    <div className="p-6">
      {openFolder ? (
        <button
          type="button"
          onClick={() => showFolder(null)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          <Trans>Tous les jeux de données</Trans>
        </button>
      ) : (
        <Link
          to="/laboratoire"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          <Trans>Laboratoire</Trans>
        </Link>
      )}

      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        {openFolder ? (
          <div className="min-w-0">
            <h1 className="flex items-center gap-3 text-3xl font-bold">
              <FolderClosed className="h-7 w-7 shrink-0 text-primary" />
              <span className="truncate">{openFolder.name}</span>
            </h1>
            <p className="text-slate-600 mt-1">
              <Plural
                value={openFolder.dataset_count}
                one="# jeu de données"
                other="# jeux de données"
              />
            </p>
          </div>
        ) : (
          <div>
            <h1 className="text-3xl font-bold">
              <Trans>Jeux de données médicaux</Trans>
            </h1>
            <p className="text-slate-600 mt-1">
              <Trans>Gérez vos collections de fichiers médicaux</Trans>
            </p>
          </div>
        )}
        {!labReadOnly && (
          <div className="flex items-center gap-2">
            {openFolder ? (
              openFolder.can_write && (
                <FolderActionsMenu
                  triggerVariant="outline"
                  onRename={() => setNaming({ folder: openFolder })}
                  onDelete={() => setDeletingFolder(openFolder)}
                />
              )
            ) : (
              <Button
                variant="outline"
                size="lg"
                onClick={() => setNaming({ folder: null })}
                className="border-slate-200 hover:border-primary/50"
              >
                <FolderPlus className="mr-2 h-4 w-4" />
                <Trans>Nouveau classeur</Trans>
              </Button>
            )}
            <Button asChild size="lg">
              <Link to={newDatasetPath}>
                <Trans>Créer un jeu de données</Trans>
              </Link>
            </Button>
          </div>
        )}
      </div>

      {/* Search and filter bar */}
      {datasetTotal > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={
                openFolder
                  ? t`Rechercher dans ce classeur...`
                  : t`Rechercher par nom ou description...`
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 border-slate-200"
            />
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant={sortField === "created_at" ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort("created_at")}
              className="text-xs"
            >
              <Trans>Date</Trans>
              {sortField === "created_at" && (
                <SortIcon className="h-3 w-3 ml-1" />
              )}
            </Button>
            <Button
              variant={sortField === "name" ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort("name")}
              className="text-xs"
            >
              <Trans>Nom</Trans>
              {sortField === "name" && <SortIcon className="h-3 w-3 ml-1" />}
            </Button>
            <Button
              variant={sortField === "example_count" ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort("example_count")}
              className="text-xs"
            >
              <Trans>Exemples</Trans>
              {sortField === "example_count" && (
                <SortIcon className="h-3 w-3 ml-1" />
              )}
            </Button>
          </div>
          {!(loading && datasets.length === 0) && (
            <span className="text-sm text-slate-400">
              <Plural
                value={datasets.length}
                one={`# affiché sur ${total}`}
                other={`# affichés sur ${total}`}
              />
            </span>
          )}
        </div>
      )}

      {showsShelf && (
        <section className="mb-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            <Trans>Classeurs</Trans>
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {folders.map((folder) => {
              const writable = !labReadOnly && folder.can_write;
              return (
                <FolderTile
                  key={folder.id}
                  folder={folder}
                  count={
                    <Plural
                      value={folder.dataset_count}
                      one="# jeu de données"
                      other="# jeux de données"
                    />
                  }
                  drop={drop}
                  onOpen={() => showFolder(folder.id)}
                  onRename={writable ? () => setNaming({ folder }) : undefined}
                  onDelete={
                    writable ? () => setDeletingFolder(folder) : undefined
                  }
                />
              );
            })}
          </div>
        </section>
      )}

      {showsShelf && (
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
          <Inbox className="h-4 w-4" />
          <Trans>Sans classeur</Trans>
        </h2>
      )}

      {datasetTotal === 0 && folders.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <h3 className="text-lg font-semibold mb-2">
            <Trans>Aucun jeu de données</Trans>
          </h3>
          <p className="text-slate-600 mb-4">
            <Trans>Commencez par créer votre premier jeu de données</Trans>
          </p>
          {!labReadOnly && (
            <Button asChild>
              <Link to="/datasets/new">
                <Trans>Créer un jeu de données</Trans>
              </Link>
            </Button>
          )}
        </div>
      ) : loading && datasets.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <p className="text-slate-500">
            <Trans>Chargement des jeux de données...</Trans>
          </p>
        </div>
      ) : datasets.length === 0 && searching ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <h3 className="text-lg font-semibold mb-2">
            <Trans>Aucun résultat</Trans>
          </h3>
          <p className="text-slate-600 mb-4">
            <Trans>
              Aucun jeu de données ne correspond à votre recherche «&nbsp;
              {search}&nbsp;»
            </Trans>
          </p>
          <Button variant="outline" onClick={() => setSearch("")}>
            <Trans>Réinitialiser la recherche</Trans>
          </Button>
        </div>
      ) : datasets.length === 0 ? (
        <p className="rounded-lg border-2 border-dashed py-8 text-center text-sm text-slate-500">
          {openFolder ? (
            <Trans>
              Ce classeur est vide. Rangez-y un jeu de données depuis le menu de
              sa carte.
            </Trans>
          ) : (
            <Trans>
              Tous les jeux de données sont rangés dans un classeur.
            </Trans>
          )}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {datasets.map((dataset) => (
              <DatasetCard
                key={dataset.id}
                dataset={dataset}
                // A sealed reference's passages are its registry: it is unsealed before it is deleted.
                onDelete={
                  labReadOnly || dataset.state === "sealed"
                    ? undefined
                    : handleDeleteDataset
                }
                onEdit={labReadOnly ? undefined : handleEditDataset}
                onMove={labReadOnly ? undefined : () => setMoving(dataset)}
                onUnfile={
                  labReadOnly || !dataset.folder_id
                    ? undefined
                    : () => void fileFromCard(dataset, null)
                }
                onDragStart={
                  labReadOnly
                    ? undefined
                    : (event) => drop.startDrag(dataset, event)
                }
                onDragEnd={drop.endDrag}
                dragging={drop.dragged?.id === dataset.id}
                folderName={
                  searching && !openFolder && dataset.folder_id
                    ? folderNames.get(dataset.folder_id)
                    : undefined
                }
              />
            ))}
          </div>
          {failed ? (
            <p className="mt-6 text-center text-sm text-red-500">
              <Trans>Erreur lors du chargement des jeux de données</Trans>
            </p>
          ) : (
            datasets.length < total && (
              <div className="mt-6 flex justify-center">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={loading}
                  className="border-slate-200 hover:border-primary/50"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Trans>Afficher plus</Trans>
                </Button>
              </div>
            )
          )}
        </>
      )}

      <FolderNameDialog
        open={naming !== null}
        onOpenChange={(open) => !open && setNaming(null)}
        initialName={naming?.folder?.name ?? ""}
        description={
          <Trans>
            Un classeur regroupe des jeux de données. Toute l'équipe de la
            spécialité le voit.
          </Trans>
        }
        placeholder={t`Ex. : Campagne MED-33`}
        onSubmit={async (name) => {
          if (naming?.folder) {
            await renameFolder(naming.folder.id, name);
          } else {
            await createFolder(name);
          }
          reloadShelf();
        }}
      />
      <DeleteFolderDialog
        folder={deletingFolder}
        description={
          <Trans>
            Supprimer le classeur « {deletingFolder?.name} » ? Les jeux de
            données qu'il contient ne sont pas supprimés : ils retournent dans
            « Sans classeur », pour toute l'équipe.
          </Trans>
        }
        deleteFolder={deleteFolder}
        onClose={() => setDeletingFolder(null)}
        onDeleted={() => {
          // Its datasets now sit in « Sans classeur »: back to the shelf, or re-read the list on it.
          if (deletingFolder?.id === openFolderId) showFolder(null);
          else setListVersion((version) => version + 1);
          reloadShelf();
        }}
      />
      {drop.dragged && (
        <FolderDropBar
          folders={folders}
          unfiled={UNFILED}
          drop={drop}
          emptyText={<Trans>Aucun classeur de cette spécialité</Trans>}
        />
      )}
      <MoveToFolderDialog
        item={moving}
        // A classeur holds the datasets of its own speciality only.
        folders={folders.filter(
          (folder) => folder.specialite === (moving?.specialite ?? null),
        )}
        description={
          <Trans>« {moving?.name} » sera rangé pour toute l'équipe.</Trans>
        }
        placeholder={t`Ex. : Campagne MED-33`}
        createFolder={createFolder}
        fileItem={(folderId) =>
          moving ? fileDataset(moving.id, folderId) : Promise.resolve()
        }
        onClose={() => setMoving(null)}
        onMoved={(folderId) => {
          if (moving) handleMoved(moving.id, folderId);
        }}
      />
    </div>
  );
}
